// src/App.js
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import React from 'react';

import Navbar from './components/Navbar/Navbar';
import TabBar from './components/TabBar/TabBar'; // New import
import Footer from './components/Footer/Footer';

import SignUp from './components/SignUp';
import Login from './components/Login';
import Profile from './components/Profile';
import PrimaryPlaceSelector from './components/PrimaryPlaceSelector';
import SecondaryPlaceAdder from './components/SecondaryPlaceAdder';
import PairingsList from './components/PairingsList';
import HeartIcon from './components/icons/HeartIcon';

import { usePlaces } from './context/PlaceContext';
import { useAuth } from './context/AuthContext';
import { savePlaceAsFavorite, removePlaceAsFavorite } from './services/api';

import PlaceSlugPage from './pages/PlaceSlugPage';
import AdminPanel from './pages/AdminPanel';
import FavoritesPage from './pages/FavoritesPage'; // New import
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import About from './pages/About';

import './App.css';

// In src/App.js
const supportedMetros = [
  'Atlanta',
  'Austin',
  'Boston',
  'Chicago',
  'Denver',
  'Detroit',
  'Joshua Tree',     // New
  'Las Vegas',       // New
  'Los Angeles',
  'Miami',
  'Nashville',       // New
  'New York City',
  'Philadelphia',    // New
  'Phoenix',         // New
  'Portland',
  'San Francisco',
  'Seattle',
  'Washington, D.C.'
];

function App() {
  const { user, getToken } = useAuth();
  const {
    primaryPlace,
    pairings,
    loadingPairings,
    savedPlaces,
    loadUserFavorites,
  } = usePlaces();

  const userId = user?.id || null;
  
  const isPrimaryFavorited = !!savedPlaces.some(
    (fav) => fav.place_internal_id === primaryPlace?.internalId
  );

  const handleTogglePrimaryFavorite = async () => {
    if (!userId) {
      alert('You must be logged in to save/un-save this place.');
      return;
    }
    if (!primaryPlace?.internalId) {
      console.warn('[App] No primaryPlace.internalId available');
      return;
    }

    try {
      const token = getToken();
      if (isPrimaryFavorited) {
        const ok = window.confirm(`Remove "${primaryPlace.name}" from favorites?`);
        if (!ok) return;
        await removePlaceAsFavorite(token, primaryPlace.internalId);
      } else {
        await savePlaceAsFavorite(token, primaryPlace.internalId);
      }
      await loadUserFavorites();
    } catch (error) {
      console.error('Error toggling primary favorite:', error.message);
    }
  };

  // The home page content with microcopy placed BELOW the search bar
  const HomePage = (
    <div className="home-wrapper">
      <h1 className="home-title">isLike</h1>

      <PrimaryPlaceSelector />

      <p className="home-description">
        Name a place you love to discover spots just like it—around the corner or across the country.
      </p>

      <div className="supported-metros">
        <strong>search in:</strong> {supportedMetros.join(' • ')} — more to come
      </div>

      {primaryPlace && (
        <>
          <h2>{primaryPlace.name}</h2>
          <p>{primaryPlace.address}</p>
          <button
            onClick={handleTogglePrimaryFavorite}
            className="favorite-button"
          >
            <HeartIcon isSelected={isPrimaryFavorited} />
          </button>
        </>
      )}

      {primaryPlace && (
        <PairingsList
          pairings={pairings}
          loading={loadingPairings}
          userId={userId}
          primaryPlaceId={primaryPlace.internalId}
        />
      )}

      {primaryPlace && <SecondaryPlaceAdder />}
    </div>
  );

  return (
    <Router>
      <Navbar user={user} />

      <main>
        <Routes>
          {/* Home route */}
          <Route path="/" element={HomePage} />
          
          {/* New Favorites route */}
          <Route path="/favorites" element={<FavoritesPage />} />

          {/* All other routes inside .container */}
          <Route path="/signup" element={<div className="container"><SignUp /></div>} />
          <Route path="/login" element={<div className="container"><Login /></div>} />
          <Route path="/profile" element={<div className="container"><Profile /></div>} />
          <Route path="/p/:slug" element={<div className="container"><PlaceSlugPage /></div>} />
          <Route path="/admin" element={<div className="container"><AdminPanel /></div>} />
          <Route path="/forgot" element={<div className="container"><ForgotPassword /></div>} />
          <Route path="/reset" element={<div className="container"><ResetPassword /></div>} />
          <Route path="/terms" element={<div className="container"><TermsOfUse /></div>} />
          <Route path="/privacy" element={<div className="container"><PrivacyPolicy /></div>} />
          <Route path="/about" element={<div className="container"><About /></div>} />
        </Routes>
      </main>

      <TabBar /> {/* Add the tab bar */}
      <Footer />
    </Router>
  );
}

export default App;